import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { FETCH, STORYBOARD } from "src/query/queryKeys";
import {
  useDeleteStoryboardMutation,
  useDomainsQuery,
  usePageSeekIndexMutation,
  useStoryboardQuery,
  useStoryboardSeekIndexMutation,
  useUnpublishPageMutation,
  useUpdateStoryboardMutation,
} from "src/query/experiences/storyboardSettings/websiteSettings";
import { useStoryboardPagesQuery } from "src/query/experiences/storyboardSettings/pages";
import {
  useAddStoryboardUsersMutation,
  useRemoveStoryboardUserAccessMutation,
  useStoryboardUsersQuery,
  useUpdateStoryboardUserAccessMutation,
} from "src/query/experiences/storyboardSettings/permissions";
import { useGatingCatalogQuery } from "src/query/experiences/storyboardSettings/gating";
import {
  AddStoryboardUsersRequest,
  GatingConfigurationResponse,
  SeekIndexRequest,
  StoryboardUsersSearchRequest,
  UpdateStoryboardUserAccessRequest,
} from "src/types/experiences/storyboardSettings";
import { STORYBOARD_TYPES, StoryboardResponse, StoryboardUpdateRequest } from "src/types/experiences/storyboardSettings/storyboard";
import {
  hydrateDomains,
  hydrateDraft,
  hydrateGatingCatalog,
  hydrateStoryboard,
  hydrateStoryboardPages,
  hydrateUsers,
  resetDraft,
  resetStoryboardSettings,
  setStoryboardSettingsError,
  setStoryboardSettingsLoading,
  updateDraftField,
} from "src/redux/actions/experiences/storyboardSettings";
import {
  selectGatingConfigurations,
  selectStoryboard,
  selectStoryboardDraft,
  selectStoryboardIsDirty,
} from "src/redux/selectors/experiences/storyboardSettings";
import { computePatch } from "src/services/experiences/storyboardSettings/websiteSettings";
import { patchStoryboardInListCache } from "src/query/experiences/storyboards";
import { StoryboardItem } from "src/types/experiences/storyboards";

interface UseWebsiteSettingsOptions {
  storyboardId: string;
  usersRequest?: StoryboardUsersSearchRequest;
  owner?: boolean;
}

const matchesConfig = (
  a: GatingConfigurationResponse,
  b: GatingConfigurationResponse,
) => (a.id ?? a.uid) === (b.id ?? b.uid);

export const useWebsiteSettings = ({
  storyboardId,
  usersRequest,
  owner = true,
}: UseWebsiteSettingsOptions) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Read draft + dirty flag from Redux (no useState).
  const storyboard = useSelector(selectStoryboard);
  const draft = useSelector(selectStoryboardDraft);
  const isDirty = useSelector(selectStoryboardIsDirty);
  const gatingConfigurations = useSelector(selectGatingConfigurations);
  const gatingConfigurationsRef = useRef(gatingConfigurations);
  gatingConfigurationsRef.current = gatingConfigurations;

  const storyboardQuery = useStoryboardQuery(storyboardId);
  const domainsQuery = useDomainsQuery();
  const storyboardPagesQuery = useStoryboardPagesQuery(storyboardId);
  const usersQuery = useStoryboardUsersQuery(storyboardId, usersRequest);
  const gatingCatalogQuery = useGatingCatalogQuery({
    storyboard: storyboardId,
    published: true,
    page: 0,
    limit: 50,
  });

  const updateStoryboard = useUpdateStoryboardMutation();
  const unpublishPage = useUnpublishPageMutation();
  const deleteStoryboardMutation = useDeleteStoryboardMutation();
  const pageSeekIndex = usePageSeekIndexMutation();
  const storyboardSeekIndex = useStoryboardSeekIndexMutation();

  const addUsers = useAddStoryboardUsersMutation();
  const updateUserAccess = useUpdateStoryboardUserAccessMutation();
  const removeUserAccess = useRemoveStoryboardUserAccessMutation();

  const draftHydratedRef = useRef(false);

  useEffect(() => {
    if (!owner) return;
    draftHydratedRef.current = false;
    dispatch(resetStoryboardSettings());
  }, [owner, storyboardId, dispatch]);

  useEffect(() => {
    if (!owner) return;
    if (storyboardQuery.data) {
      dispatch(hydrateStoryboard(storyboardQuery.data));
      if (!isDirty && !draftHydratedRef.current) {
        draftHydratedRef.current = true;
        dispatch(hydrateDraft(storyboardQuery.data));
      }
    }
  }, [owner, storyboardQuery.data, isDirty, dispatch]);
  useEffect(() => {
    dispatch(
      setStoryboardSettingsLoading({ key: "storyboard", loading: storyboardQuery.isLoading }),
    );
  }, [storyboardQuery.isLoading, dispatch]);
  useEffect(() => {
    if (storyboardQuery.error) {
      dispatch(setStoryboardSettingsError({ key: "storyboard", error: storyboardQuery.error }));
    }
  }, [storyboardQuery.error, dispatch]);

  // Domains
  useEffect(() => {
    if (domainsQuery.data) dispatch(hydrateDomains(domainsQuery.data));
  }, [domainsQuery.data, dispatch]);
  useEffect(() => {
    dispatch(setStoryboardSettingsLoading({ key: "domains", loading: domainsQuery.isLoading }));
  }, [domainsQuery.isLoading, dispatch]);
  useEffect(() => {
    if (domainsQuery.error) {
      dispatch(setStoryboardSettingsError({ key: "domains", error: domainsQuery.error }));
    }
  }, [domainsQuery.error, dispatch]);

  useEffect(() => {
    const data = storyboardPagesQuery.data;
    if (!data) return;
    const items = data.records ?? data.items ?? [];
    dispatch(hydrateStoryboardPages(items));
  }, [storyboardPagesQuery.data, dispatch]);
  useEffect(() => {
    dispatch(
      setStoryboardSettingsLoading({
        key: "storyboardPages",
        loading: storyboardPagesQuery.isLoading,
      }),
    );
  }, [storyboardPagesQuery.isLoading, dispatch]);
  useEffect(() => {
    if (storyboardPagesQuery.error) {
      dispatch(
        setStoryboardSettingsError({
          key: "storyboardPages",
          error: storyboardPagesQuery.error,
        }),
      );
    }
  }, [storyboardPagesQuery.error, dispatch]);

  useEffect(() => {
    const records = gatingCatalogQuery.data?.records;
    if (records) dispatch(hydrateGatingCatalog(records));
  }, [gatingCatalogQuery.data, dispatch]);
  useEffect(() => {
    dispatch(
      setStoryboardSettingsLoading({
        key: "gatingCatalog",
        loading: gatingCatalogQuery.isLoading,
      }),
    );
  }, [gatingCatalogQuery.isLoading, dispatch]);
  useEffect(() => {
    if (gatingCatalogQuery.error) {
      dispatch(
        setStoryboardSettingsError({
          key: "gatingCatalog",
          error: gatingCatalogQuery.error,
        }),
      );
    }
  }, [gatingCatalogQuery.error, dispatch]);

  useEffect(() => {
    const pages = usersQuery.data?.pages;
    if (!pages) return;
    const items = pages.flatMap((page) => page.records ?? []);
    if (items.length > 0) dispatch(hydrateUsers(items));
  }, [usersQuery.data, dispatch]);
  
  useEffect(() => {
    dispatch(setStoryboardSettingsLoading({ key: "users", loading: usersQuery.isLoading }));
  }, [usersQuery.isLoading, dispatch]);
  useEffect(() => {
    if (usersQuery.error) {
      dispatch(setStoryboardSettingsError({ key: "users", error: usersQuery.error }));
    }
  }, [usersQuery.error, dispatch]);

  // ─── Draft mutations ────────────────────────────────────────────────────

  const setField = useCallback(
    <K extends keyof StoryboardResponse>(key: K, value: StoryboardResponse[K]) => {
      dispatch(updateDraftField({ key, value }));
    },
    [dispatch],
  );

  const discard = useCallback(() => {
    dispatch(resetDraft());
  }, [dispatch]);

  // ─── Server mutations ────────────────────────────────────────────────────

  const save = useCallback(
    (
      overrides?: StoryboardUpdateRequest,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) => {
      if (!draft) return;
      const base = storyboard ?? draft;
      const patch = computePatch(base, draft);
      const data: StoryboardUpdateRequest = { ...patch, ...overrides };

      if (
        draft.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 &&
        base.excludeFromSearch !== draft.excludeFromSearch
      ) {
        data.storyBoardLevelIndex = true;
      }
      return updateStoryboard.mutate(
        { id: draft.id, data },
        {
          onSuccess: (saved: StoryboardResponse) => {
            const merged: StoryboardResponse =
              saved.password == null && draft.password
                ? { ...saved, password: draft.password }
                : saved;
            queryClient.setQueryData([STORYBOARD, FETCH, draft.id], merged);
            // Keep the storyboards list cache in sync so renames (and other
            // edits) show immediately when navigating back, without a refetch.
            patchStoryboardInListCache(queryClient, {
              id: saved.id,
              name: saved.name,
              type: saved.type,
              modifiedDate: saved.modifiedDate,
              page: saved.page as StoryboardItem["page"],
            });
            dispatch(hydrateStoryboard(merged));
            dispatch(hydrateDraft(merged));
            callbacks?.onSuccess?.();
          },
          onError: (error: unknown) => {
            callbacks?.onError?.(error);
          },
        },
      );
    },
    [draft, storyboard, updateStoryboard, dispatch, queryClient],
  );

  const unpublish = useCallback(
    (callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => {
      const pageId = draft?.page?.id ?? storyboardQuery.data?.page?.id;
      if (!pageId) return;
      return unpublishPage.mutate(pageId, {
        onSuccess: () => callbacks?.onSuccess?.(),
        onError: (error: unknown) => callbacks?.onError?.(error),
      });
    },
    [draft, storyboardQuery.data, unpublishPage],
  );

  const deleteStoryboard = useCallback(
    (callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => {
      if (!storyboardId) return;
      return deleteStoryboardMutation.mutate(storyboardId, {
        onSuccess: () => callbacks?.onSuccess?.(),
        onError: (error: unknown) => callbacks?.onError?.(error),
      });
    },
    [storyboardId, deleteStoryboardMutation],
  );

  const indexPage = useCallback(
    (
      data: SeekIndexRequest,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) => {
      const pageId = draft?.page?.id ?? storyboardQuery.data?.page?.id;
      if (!pageId) return;
      return pageSeekIndex.mutate(
        { pageId, data },
        {
          onSuccess: () => callbacks?.onSuccess?.(),
          onError: (error: unknown) => callbacks?.onError?.(error),
        },
      );
    },
    [draft, storyboardQuery.data, pageSeekIndex],
  );

  const indexStoryboard = useCallback(
    (
      data: SeekIndexRequest,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) =>
      storyboardSeekIndex.mutate(
        { storyboardId, data },
        {
          onSuccess: () => callbacks?.onSuccess?.(),
          onError: (error: unknown) => callbacks?.onError?.(error),
        },
      ),
    [storyboardId, storyboardSeekIndex],
  );

  const addGatingConfig = useCallback(
    (data: GatingConfigurationResponse, silent?: boolean) => {
      const current = gatingConfigurationsRef.current;
      const next = data.smart ? [data, ...current] : [...current, data];
      dispatch(
        updateDraftField({ key: "gatingConfigurations", value: next, silent }),
      );
    },
    [dispatch],
  );

  const editGatingConfig = useCallback(
    (id: string, data: GatingConfigurationResponse, silent?: boolean) => {
      const next = gatingConfigurationsRef.current.map((g) => {
        const matchesById = id && (g.id === id || g.uid === id);
        if (matchesById || matchesConfig(g, data)) {
          return { ...g, ...data };
        }
        return g;
      });
      dispatch(
        updateDraftField({ key: "gatingConfigurations", value: next, silent }),
      );
    },
    [dispatch],
  );

  const removeGatingConfig = useCallback(
    (id: string) => {
      const next = gatingConfigurationsRef.current.filter(
        (g) => g.uid !== id && g.id !== id,
      );
      dispatch(updateDraftField({ key: "gatingConfigurations", value: next }));
    },
    [dispatch],
  );

  const addPermissions = useCallback(
    (data: AddStoryboardUsersRequest) =>
      addUsers.mutate({ storyboardId, data }),
    [addUsers, storyboardId],
  );
  const changeUserRole = useCallback(
    (storyboardId: string, data: UpdateStoryboardUserAccessRequest[]) =>
      updateUserAccess.mutate({ storyboardId, data }),
    [updateUserAccess],
  );
  const removeUser = useCallback(
    (userAccessId: string) => removeUserAccess.mutate(userAccessId),
    [removeUserAccess],
  );

  return {
    setField,
    save,
    discard,
    unpublish,
    deleteStoryboard,
    indexPage,
    indexStoryboard,
    addGatingConfig,
    editGatingConfig,
    removeGatingConfig,
    addPermissions,
    changeUserRole,
    removeUser,

    isSaving: updateStoryboard.isPending,
    isUnpublishing: unpublishPage.isPending,
    isDeleting: deleteStoryboardMutation.isPending,
    isIndexingPage: pageSeekIndex.isPending,
    isIndexingStoryboard: storyboardSeekIndex.isPending,
  };
};
